import React, { useState } from 'react';
import { Accessibility, Mic, Eye, Type } from 'lucide-react';

interface AccessibilityToggleProps {
  onVoiceNavigationToggle: () => void;
  isVoiceNavigationEnabled: boolean;
}

const AccessibilityToggle: React.FC<AccessibilityToggleProps> = ({
  onVoiceNavigationToggle,
  isVoiceNavigationEnabled
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [largeText, setLargeText] = useState(false);

  // Toggle high contrast mode
  const toggleHighContrast = () => {
    setHighContrast(!highContrast);
    document.documentElement.classList.toggle('high-contrast', !highContrast);
  };

  // Toggle large text
  const toggleLargeText = () => {
    setLargeText(!largeText);
    document.documentElement.classList.toggle('large-text', !largeText);
  };

  return (
    <div className="hidden">
      {/* Accessibility Styles */}
      <style>{`
        .top-4 {
          top: 0.1rem;
        }
        
        .accessibility-button {
          top: auto !important;
          bottom: 1rem !important;
        }
        
        .high-contrast {
          --slate-50: #ffffff;
          --slate-100: #f0f0f0;
          --slate-200: #e0e0e0;
          --slate-300: #d0d0d0;
          --slate-400: #a0a0a0;
          --slate-500: #808080;
          --slate-600: #606060;
          --slate-700: #404040;
          --slate-800: #202020;
          --slate-900: #000000;
        }
        
        .high-contrast * {
          border-color: #000000 !important;
        }
        
        .high-contrast button,
        .high-contrast a {
          outline: 2px solid #000000 !important;
          outline-offset: 2px !important;
        }
        
        .large-text {
          font-size: 125% !important;
        }
        
        .large-text h1 { font-size: 3.5rem !important; }
        .large-text h2 { font-size: 2.5rem !important; }
        .large-text h3 { font-size: 2rem !important; }
        .large-text h4 { font-size: 1.5rem !important; }
        .large-text p, .large-text span, .large-text div { font-size: 1.25rem !important; }
        .large-text button { font-size: 1.25rem !important; padding: 1rem 1.5rem !important; }
      `}</style>

      {/* Accessibility Toggle Button */}
      <div className="fixed right-4 z-40 accessibility-button hidden">
        <div className="relative">
          {/* Main Toggle Button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="bg-slate-900 text-white p-2 rounded-full shadow-lg hover:bg-slate-800 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-slate-300 flex items-center justify-center w-10 h-10"
            title="Accessibility Options"
            aria-label="Open accessibility options"
          >
            <Accessibility className="w-4 h-4" />
          </button>

          {/* Expanded Options Panel */}
          {isExpanded && (
            <div className="absolute bottom-12 -right-2 sm:right-0 bg-white rounded-lg shadow-xl border border-slate-200 p-4 w-80 max-w-[calc(100vw-2rem)] sm:max-w-none z-50">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Accessibility Options
                </h3>
                <p className="text-sm text-slate-600">
                  Customize your experience for better accessibility
                </p>
              </div>

              <div className="space-y-4">
                {/* Voice Navigation Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center">
                    <Mic className="w-5 h-5 text-slate-600 mr-3" />
                    <div>
                      <div className="font-medium text-slate-900">Voice Navigation</div>
                      <div className="text-sm text-slate-600">
                        Navigate using voice commands
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={onVoiceNavigationToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 ${
                      isVoiceNavigationEnabled ? 'bg-slate-900' : 'bg-slate-300'
                    }`}
                    role="switch"
                    aria-checked={isVoiceNavigationEnabled}
                    aria-label="Toggle voice navigation"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isVoiceNavigationEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* High Contrast Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center">
                    <Eye className="w-5 h-5 text-slate-600 mr-3" />
                    <div>
                      <div className="font-medium text-slate-900">High Contrast</div>
                      <div className="text-sm text-slate-600">
                        Increase contrast for better visibility
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={toggleHighContrast}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 ${
                      highContrast ? 'bg-slate-900' : 'bg-slate-300'
                    }`}
                    role="switch"
                    aria-checked={highContrast}
                    aria-label="Toggle high contrast mode"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        highContrast ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Large Text Toggle */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center">
                    <Type className="w-5 h-5 text-slate-600 mr-3" />
                    <div>
                      <div className="font-medium text-slate-900">Large Text</div>
                      <div className="text-sm text-slate-600">
                        Increase text size for easier reading
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={toggleLargeText}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 ${
                      largeText ? 'bg-slate-900' : 'bg-slate-300'
                    }`}
                    role="switch"
                    aria-checked={largeText}
                    aria-label="Toggle large text mode"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        largeText ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Voice Navigation Help */}
                {isVoiceNavigationEnabled && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-800">
                      <div className="font-medium mb-1">Voice Commands:</div>
                      <div className="space-y-1 text-xs">
                        <div>• "Go home" - Navigate to homepage</div>
                        <div>• "About us" - Go to about page</div>
                        <div>• "Services" - View services</div>
                        <div>• "Schedule appointment" - Book appointment</div>
                        <div>• "Call clinic" - Call the clinic</div>
                        <div>• "Help" - List all commands</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setIsExpanded(false)}
                  className="w-full bg-slate-900 text-white py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccessibilityToggle;